<template>
  <form class="hero-banner-search" role="search" :class="{ 'search-bar-component': isHeaderMode }">
    <div class="input-what-wrapper" ref="whatForm" @keyup="whatFormKeyUp">
      <input ref="inputWhat" type="text" v-model="searchText"
        @focus="whatCursorIndex = -1; focusInInput(); searchInput();" role="combobox" aria-controls="whatAutoComplete"
        aria-autocomplete="both" :aria-haspopup="displayListSearch" :aria-expanded="displayListSearch"
        aria-label="Rechercher une information, un professionnel, un établissement (dernières recherches et recherches fréquentes en ce moment disponibles ci-après)"
        class="input-what input-what-pos" :placeholder="searchPlaceholderTitle" :title="searchPlaceholderTitle"
        v-on:keyup.enter="hasWhatCursor = onEnterInput(whatCursorIndex, hasWhatCursor)" />

      <button v-if="searchText" @click="searchText = ''" type="button" class="clear-item-search btn-close">
        <i class="icon-close-circle-solid" aria-hidden="true"></i>
        <span class="sr-only">Supprimer</span>
      </button>

      <span class="sp-autocomplete-results sr-only" aria-live="polite">
        <template v-if="displayListSearch">
          <span v-if="searchListHistoyFiltred?.length">{{ searchListHistoyFiltred.length }} dernières recherches</span>
          <span v-if="searchListHistoyFiltred?.length && searchListApi?.length"> et </span>
          <span v-if="searchListApi?.length">{{ searchListApi.length }} recherches suggérées</span>
        </template>
      </span>

      <WhatAutoComplete v-if="displayListSearch" :cursorIndex="whatCursorIndex" :displayListSearch="displayListSearch"
        :searchListHistoyFiltred="searchListHistoyFiltred.slice(0, 4)" :searchListApi="searchListApi.slice(0, 3)"
        @onRemoveSearchItem="$emit('onRemoveSearchItem', $event)" @onClearSearchHistory="$emit('onClearSearchHistory')"
        @onClickSearchText="searchText = $event" :searchText="searchText" :HistoryHasSearch="searchText.length >= 3"
        @closeList="closeSearchList" />
    </div>

    <div class="input-where-wrapper" ref="whereForm">
      <i class="icon" :class="locationText === 'Autour de moi' ? 'icon-geolocation' : 'icon-location'"></i>
      <input type="text" class="input-where" v-model="locationText" @focus="displayListLocations = true"
        @keyup="displayListLocations = true; locationInput()"
        @keyup.down="whereCursorIndex = newCursorWhereIndex(whereCursorIndex, 'down'); displayListLocations = true"
        @keyup.up="whereCursorIndex = newCursorWhereIndex(whereCursorIndex, 'up');"
        @keyup.esc="locationText = ''; displayListLocations = false;"
        @keyup.end="hasWhereCursor = false; whereCursorIndex = -1" role="combobox" aria-controls="whereAutoComplete"
        aria-autocomplete="both" :aria-haspopup="displayListLocations" :aria-expanded="displayListLocations"
        aria-label="Rechercher une ville (dernières recherches et recherches fréquentes en ce moment disponibles ci-après)"
        ref="locationInputRef" :placeholder="searchPlaceholderWhere"
        v-on:keyup.enter="hasWhereCursor = onEnterWhereInput(whereCursorIndex)" />

      <button v-if="locationText" type="button" class="clear-item-search btn-close">
        <i class="icon-close-circle-solid" @click="locationText = ''" aria-hidden="true"></i>
        <span class="sr-only">Supprimer</span>
      </button>

      <WhereAutoComplete :class="{ 'what-input-mobile-external-place': isScreenMdMin && isExternalMode }"
        :cursorIndex="whereCursorIndex" :displayListLocations="displayListLocations" :locationListApi="locationListApi"
        :locationListHistoyFiltred="locationListHistoyFiltred" @onClearLocationHistory="$emit('onClearLocationHistory')"
        @onRemoveLocationItem="$emit('onRemoveLocationItem', $event)" @onClickLocationText="locationText = $event; displayListLocations = false" />
    </div>

    <template v-if="isScreenMdMin && isExternalMode">
      <button type="button" class="btn-search btn-search-mobile" @click.prevent="search" ref="searchBtnRef"
        :disabled="searchText?.length < 2" aria-label="Rechercher">
        <i class="icon-search" aria-hidden="true"></i>
      </button>
      <button class="search-item-around" type="button"
        @click.prevent="locationText = 'Autour de moi'; locationInputRef.focus()">
        <i class="icon-geolocation" aria-hidden="true"></i> Autour de moi
      </button>
    </template>

    <button type="button" v-else-if="!isHeaderMode || !isScreenMdMin" class="btn-search"
      :disabled="searchText?.length < 2" @click.prevent="search()">
      <i class="icon-search visible-lg" aria-hidden="true"></i>
      <span class="sr-only-desktop">Rechercher</span>
    </button>
  </form>
</template>

<script>
import {
  computed, inject, ref, nextTick,
} from 'vue';
import { onClickOutside } from '@vueuse/core';
import WhatAutoComplete from '@/components/homePage/autoCompleteComponents/WhatAutoComplete.component.vue';
import WhereAutoComplete from '@/components/homePage/autoCompleteComponents/WhereAutoComplete.component.vue';
import { debounce, htmlStripper } from '@/helpers';

export default {
  components: {
    WhatAutoComplete,
    WhereAutoComplete,
  },
  emits: [
    'onSearch',
    'onClearSearchHistory',
    'onRemoveSearchItem',
    'onClearLocationHistory',
    'onRemoveLocationItem',
    'onSearchinput',
    'onLocationinput',
  ],
  props: {
    searchListHistory: {
      type: Array,
      default: () => [],
    },
    searchListApi: {
      type: Array,
      default: () => [],
    },
    locationListHistory: {
      type: Array,
      default: () => [],
    },
    locationListApi: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const displayListSearch = ref(false);
    const what = inject('what');
    const whatCursorIndex = ref(-1);
    const hasWhatCursor = ref(false);
    const where = inject('where');
    const searchText = ref(what);
    const whereCursorIndex = ref(-1);
    const hasWhereCursor = ref(false);
    const searchListHistoyFiltred = computed(() => (searchText.value.length < 3 ? props.searchListHistory
      : props.searchListHistory.filter((elem) => elem.indexOf(searchText.value) === 0)));
    const searchInput = debounce(() => {
      // Don't refetch suggested search if we already did.
      if (props.searchListApi?.length && searchText.value?.length < 3) return;
      emit('onSearchinput', encodeURIComponent(searchText.value));
    });

    const searchPlaceholders = inject('searchPlaceholders');
    const searchPlaceholderTitle = searchPlaceholders.text_desktop;
    const searchPlaceholderWhere = searchPlaceholders.text_where;

    const displayListLocations = ref(false);
    const locationText = ref(where);
    const locationListHistoyFiltred = computed(() => (locationText.value.length < 3 ? props.locationListHistory : []));
    const locationInput = debounce(() => {
      emit('onLocationinput', locationText.value.length > 2 ? locationText.value : null);
    });

    const isScreenMdMin = inject('isScreenMdMin');
    const isHeaderMode = inject('isHeaderMode');
    const inputWhat = ref('');
    const isExternalMode = inject('isExternalMode');
    const focusOutLocationInput = debounce(() => { displayListLocations.value = false; });
    const focusInInput = debounce(() => nextTick(() => { displayListSearch.value = true; }));
    const focusOutInput = debounce(() => { displayListSearch.value = false; });
    const locationInputRef = ref('');
    const searchBtnRef = ref('');

    // handle hide autocomplete
    const whatForm = ref(null);
    const whereForm = ref(null);
    onClickOutside(whatForm, focusOutInput);
    onClickOutside(whereForm, focusOutLocationInput);

    function search() {
      if (searchText.value.length >= 3) {
        emit('onSearch', { searchText: encodeURIComponent(searchText.value), locationText: locationText.value });
      }
    }
    function newCursorIndex(index, event) {
      let offset = 0;

      // BTN delete history
      if (searchListHistoyFiltred.value.length && searchText.value.length < 3) {
        offset += 1;
      }

      const totalDisplayedElements = (props.searchListApi.slice(0, 3).length) + (searchListHistoyFiltred.value.slice(0, 4).length) + offset;

      if (event === 'down') {
        return ((index + 1) < totalDisplayedElements) ? (index + 1) : 0;
      }
      hasWhatCursor.value = true;
      return (index > 0) ? (index - 1) : totalDisplayedElements - 1;
    }

    function onEnterInput(index) {
      if (whatCursorIndex.value === -1) {
        search();
      }
      if (searchListHistoyFiltred.value?.length > 0 && index === searchListHistoyFiltred.value.length) {
        emit('onClearSearchHistory');
      } else {
        const indexSearchListApi = index - (Array.isArray(searchListHistoyFiltred.value) ? (searchListHistoyFiltred.value.slice(0, 4).length + 1) : 0);
        const historyItem = searchListHistoyFiltred.value?.[index];
        const apiItem = props.searchListApi?.[indexSearchListApi];
        searchText.value = historyItem || apiItem || searchText.value;
      }
      whatCursorIndex.value = -1;
      displayListSearch.value = false;
      return false;
    }
    const closeSearchList = () => {
      displayListSearch.value = false;
    };

    function newCursorWhereIndex(index, event) {
      const totalDisplayedElements = (props.locationListApi.slice(0, 5).length) + (locationListHistoyFiltred.value.slice(0, 3).length);
      if (event === 'down') {
        return (index < totalDisplayedElements) ? (index + 1) : 0;
      }
      hasWhereCursor.value = true;
      return (index > 0) ? (index - 1) : totalDisplayedElements;
    }

    function onEnterWhereInput(index) {
      if (whereCursorIndex.value === -1) {
        search();
      } else if (index === (props.locationListApi || []).length) {
        locationText.value = 'Autour de moi';
      } else {
        locationText.value = htmlStripper(props.locationListApi[index] || locationListHistoyFiltred.value?.[index - 1]);
      }
      whereCursorIndex.value = -1;
      displayListLocations.value = false;
      return false;
    }

    const whatFormKeyUp = ($event) => {
      if ($event.key === 'ArrowDown') {
        whatCursorIndex.value = newCursorIndex(whatCursorIndex.value, 'down');
      } else if ($event.key === 'ArrowUp') {
        whatCursorIndex.value = newCursorIndex(whatCursorIndex.value, 'up');
      } else if ($event.key === 'Escape') {
        searchText.value = '';
        whatCursorIndex.value = -1;
        displayListSearch.value = false;
        inputWhat.value.focus();
      } else if ($event.key === 'End') {
        hasWhatCursor.value = false;
        whatCursorIndex.value = -1;
      } else if ($event.key === 'Enter') {
        hasWhatCursor.value = false;
        whatCursorIndex.value = -1;
        displayListSearch.value = false;
        search();
      } else {
        displayListSearch.value = true;
        whatCursorIndex.value = -1;
        searchInput();
      }
    };

    return {
      displayListSearch,
      searchText,
      searchListHistoyFiltred,
      searchPlaceholderTitle,
      searchPlaceholderWhere,
      debounce,
      searchInput,
      htmlStripper,
      locationText,
      displayListLocations,
      locationListHistoyFiltred,
      locationInput,
      isScreenMdMin,
      isHeaderMode,
      inputWhat,
      focusOutLocationInput,
      focusInInput,
      focusOutInput,
      isExternalMode,
      locationInputRef,
      searchBtnRef,
      search,
      newCursorIndex,
      whatCursorIndex,
      hasWhatCursor,
      onEnterInput,
      whereCursorIndex,
      hasWhereCursor,
      newCursorWhereIndex,
      onEnterWhereInput,
      whatForm,
      whereForm,
      whatFormKeyUp,
      closeSearchList,
    };
  },
};
</script>

<style lang="scss">
.search-bar-component {
  .short-search-wrapper {
    z-index: 99;
  }
}

.hero-banner-search {
  .btn-search-mobile {
    margin: 0px !important;
    padding: 10px 10px !important;
    border-radius: 0px !important;
  }

  .what-input-mobile-external-place {
    top: 168px !important;
  }
}
</style>
